import React, { FC, useState, useEffect, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import {
  Container,
  Description,
  FormFieldContainer,
  FormWrapper,
  Subtitle,
  SurveyDescriptionContainer,
  SurveyDescriptionImageContainer,
  SurveyDescriptionTextContainer,
  Title,
  TitlePlaceholder,
  SubtitlePlaceholder,
  DescriptionPlaceholder,
  Label,
  ControllerCont,
  RadioButtonBlock,
  RadioButtonContainer,
} from './CreateNewSurveyForm.styles';
import { useAuth } from '../../hooks/useAuth';
import { IOrganizations, ProjectEntity } from '../../services/organisations';
import { InputLabel } from '../../shared/Input/Input.styles';
import RadioButtonComponent from '../../shared/Radio/Radio';
import SearchDropdown, { ISearchDropdownItem } from '../SearchDropdown/SearchDropdown';
import SearchInputHeader from '../SearchDropdown/SearchInputHeader/SearchInputHeader';
import AdaptIcon from '@/assets/icons/adapt.svg?react';
import EffectIcon from '@/assets/icons/effect.svg?react';
import GoodTeamIcon from '@/assets/icons/good_team.svg?react';
import PulseIcon from '@/assets/icons/pulse.svg?react';
import { formatSurveyTypeLabel } from '../../constans/forms';

interface ImageSwitcherType {
  ADAPT: React.ReactElement;
  PULSE: React.ReactElement;
  EFFECT: React.ReactElement;
  GOOD_TEAM: React.ReactElement;
}

const imagesSwitcher: ImageSwitcherType = {
  ADAPT: <AdaptIcon />,
  PULSE: <PulseIcon />,
  EFFECT: <EffectIcon />,
  GOOD_TEAM: <GoodTeamIcon />,
};
const getDefaultValues = (survey: string) => ({
  survey,
  name: '',
  copySurvey: 'no',
  organization: '',
  copyFrom: '',
  copyManagerAccess: 'no',
});

interface CreateNewSurveyFormProps {
  submit: (data: ReturnType<typeof getDefaultValues>) => void;
  apiError?: Record<string, string | string[]>;
  organizationError?: { error: string; set: (error: string) => void };
  allOrganizations?: IOrganizations[];
  allProjects?: ProjectEntity[];
  setAllProjects: (projects: ProjectEntity[]) => void;
  getProjects: (organizationId: number) => Promise<ProjectEntity[]>;
}

type TCreateSurveyFormValues = ReturnType<typeof getDefaultValues>;

const CreateNewSurveyForm: FC<CreateNewSurveyFormProps> = ({
  submit,
  organizationError,
  apiError,
  allOrganizations = [],
  allProjects = [],
  setAllProjects,
  getProjects,
}) => {
  const [filteredOrganizations, setFilteredOrganizations] = useState(allOrganizations);
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const { isEBHRMember, allowedSurveys = [], refreshUserInfo } = useAuth();

  useEffect(() => {
    // needs to happen to refresh allowed surveys
    if (!isEBHRMember && refreshUserInfo) {
      refreshUserInfo();
    }
  }, [isEBHRMember]);

  const defaultSchema = object({
    ...(isEBHRMember && {
      organization: string().required(t('createSurveyModal.form.validation.general')),
    }),
    survey: string().required(t('createSurveyModal.form.validation.general')),
    name: string()
      .required(t('createSurveyModal.form.validation.name'))
      .matches(
        /^[^&$@=;/:+,?+\\{}^%`[\]~"'<>#|]*$/,
        t('createSurveyModal.form.validation.nameForbidenChars')
      ),
    copySurvey: string().required(),
    copyFrom: string().when('copySurvey', {
      is: (val: string) => val === 'yes',
      then: string().required(t('createSurveyModal.form.validation.general')),
      otherwise: string().notRequired(),
    }),
  });

  const { control, handleSubmit, watch, setValue, formState } = useForm<TCreateSurveyFormValues>({
    defaultValues: getDefaultValues(isEBHRMember ? '' : allowedSurveys[0] || ''),
    resolver: yupResolver(defaultSchema) as unknown as Resolver<TCreateSurveyFormValues>,
  });

  const errors = formState.errors;

  const survey = watch('survey');
  const organization = watch('organization');
  const copySurveyValue = watch('copySurvey');
  const copyFromValue = watch('copyFrom');

  const filteredProjects = survey ? allProjects.filter(project => project.type === survey) : [];

  useEffect(() => {
    if (copySurveyValue === 'no') {
      setValue('copyFrom', '');
      setValue('copyManagerAccess', 'no');
    }
  }, [copySurveyValue, setValue]);

  useEffect(() => {
    if (!copyFromValue) {
      setValue('copyManagerAccess', 'no');
    }
  }, [copyFromValue, setValue]);

  useEffect(() => {
    setValue('copySurvey', 'no');
    setValue('copyFrom', '');
  }, [survey, setValue]);

  useEffect(() => {
    if (!organization) return;

    setValue('copySurvey', 'no');
    setValue('copyFrom', '');

    if (isEBHRMember && organization && getProjects) {
      const selectedOrg = allOrganizations.find(org => org.name === organization);
      if (selectedOrg?.id) {
        getProjects(Number(selectedOrg.id)).then(resp => {
          setAllProjects(resp);
        });
      }
    }
  }, [organization]);

  const allowedOrgSurveys = !isEBHRMember
    ? allowedSurveys
    : allOrganizations.find(org => org.name === organization)?.allowed_surveys;

  const doFilter = (phrase: string) => {
    if (phrase) {
      setFilteredOrganizations(
        allOrganizations.filter(organization =>
          organization.name.toLowerCase().includes(phrase.toLowerCase())
        )
      );
    } else {
      setFilteredOrganizations(allOrganizations);
    }
  };

  const getErrorMessage = (fieldError: unknown): string | undefined => {
    if (Array.isArray(fieldError)) {
      return fieldError[0];
    } else if (typeof fieldError === 'string') {
      return fieldError;
    }
    return undefined;
  };

  const onSubmit = (data: ReturnType<typeof getDefaultValues>) => {
    submit(data);
  };

  return (
    <FormWrapper onSubmit={handleSubmit(onSubmit)}>
      <Container>
        {isEBHRMember && (
          <FormFieldContainer>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchDropdown
                  items={filteredOrganizations.map((org) => ({
                    id: String(org.id),
                    name: org.name,
                  }))}
                  placeholder={t('createSurveyModal.form.placeholders.organization')}
                  activeEl={
                    filteredOrganizations
                      .map((org) => ({ id: String(org.id), name: org.name }))
                      .find((org) => org.name === value)
                  }
                  selectEl={(data: string | ISearchDropdownItem) => {
                    organizationError?.set('');
                    setValue('survey', '');
                    onChange(typeof data === 'string' ? data : data.name);
                  }}
                  label={t('createSurveyModal.form.labels.organization')}
                  error={errors?.organization?.message || organizationError?.error}
                  searchMode={true}
                  doFilter={doFilter}
                />
              )}
              name="organization"
            />
          </FormFieldContainer>
        )}
        <FormFieldContainer>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchDropdown
                items={(allowedOrgSurveys || []).map(survey => ({
                  id: survey,
                  name: formatSurveyTypeLabel(survey),
                }))}
                placeholder={t('createSurveyModal.form.placeholders.survey')}
                activeEl={(allowedOrgSurveys || [])
                  .map(survey => ({
                    id: survey,
                    name: formatSurveyTypeLabel(survey),
                  }))
                  .find(survey => survey.id === value)}
                selectEl={(selected: string | ISearchDropdownItem) =>
                  onChange(typeof selected === 'string' ? selected : selected.id)
                }
                selectFullObject
                label={t('createSurveyModal.form.labels.survey')}
                error={errors?.survey?.message}
              />
            )}
            name="survey"
          />
        </FormFieldContainer>
        <FormFieldContainer>
          <Label>{t('createSurveyModal.form.textAreaLabel')}</Label>
          <ControllerCont>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <SearchInputHeader
                  placeholder={t('createSurveyModal.form.textAreaPlaceholder')}
                  onChange={onChange}
                  defaultValue={value}
                  active={false}
                  inputRef={inputRef}
                  noDrop
                  error={errors?.name?.message || getErrorMessage(apiError?.name)}
                />
              )}
              name="name"
            />
          </ControllerCont>
        </FormFieldContainer>
        {filteredProjects.length > 0 && (
          <FormFieldContainer>
            <Controller
              control={control}
              name="copySurvey"
              defaultValue="no"
              render={({ field }) => (
                <RadioButtonBlock>
                  <InputLabel>{t('createSurveyModal.form.labels.copy')}</InputLabel>
                  <RadioButtonContainer>
                    <RadioButtonComponent
                      options={[
                        { label: t('createSurveyModal.form.labels.yes'), value: 'yes' },
                        { label: t('createSurveyModal.form.labels.no'), value: 'no' },
                      ]}
                      value={field.value}
                      direction="horizontal"
                      onChange={field.onChange}
                    />
                  </RadioButtonContainer>
                </RadioButtonBlock>
              )}
            />
          </FormFieldContainer>
        )}
        {copySurveyValue === 'yes' && (
          <FormFieldContainer>
            <Controller
              control={control}
              name="copyFrom"
              render={({ field: { onChange, value } }) => (
                <SearchDropdown
                  items={filteredProjects.map((project) => ({
                    id: String(project.id),
                    name: project.name,
                  }))}
                  placeholder={t('createSurveyModal.form.placeholders.copyProject')}
                  activeEl={filteredProjects
                    .map((project) => ({ id: String(project.id), name: project.name }))
                    .find((project) => project.id === String(value))}
                  selectEl={(selected: string | ISearchDropdownItem) => {
                    const selectedProject = filteredProjects.find(
                      p => p.name === (typeof selected === 'string' ? selected : selected.name)
                    );
                    onChange(selectedProject?.id || '');
                  }}
                  label={t('createSurveyModal.form.labels.copyProject')}
                  error={errors?.copyFrom?.message}
                />
              )}
            />
          </FormFieldContainer>
        )}
        {copySurveyValue === 'yes' && copyFromValue && (
          <FormFieldContainer>
            <Controller
              control={control}
              name="copyManagerAccess"
              defaultValue="no"
              render={({ field }) => (
                <RadioButtonBlock>
                  <InputLabel>{t('createSurveyModal.form.labels.copyManagerAccess')}</InputLabel>
                  <RadioButtonContainer>
                    <RadioButtonComponent
                      options={[
                        { label: t('createSurveyModal.form.labels.yes'), value: 'yes' },
                        { label: t('createSurveyModal.form.labels.no'), value: 'no' },
                      ]}
                      value={field.value}
                      direction="horizontal"
                      onChange={field.onChange}
                    />
                  </RadioButtonContainer>
                </RadioButtonBlock>
              )}
            />
          </FormFieldContainer>
        )}
        <button data-testid="hiddenSubmitButton" id="submitCreateSurveyForm" type="submit" hidden>
          Hidden submit button
        </button>
      </Container>
      <Controller
        control={control}
        render={({ field: { value } }) => (
          <SurveyDescriptionContainer>
            <SurveyDescriptionImageContainer data-testid="descHeader">
              {imagesSwitcher[(value || 'ADAPT') as keyof ImageSwitcherType]}
            </SurveyDescriptionImageContainer>
            <SurveyDescriptionTextContainer>
              {value ? (
                <Title>{t(`createSurveyModal.${value}.surveyName`)}</Title>
              ) : (
                <TitlePlaceholder />
              )}
              {value ? (
                <Subtitle>{t('createSurveyModal.description')}</Subtitle>
              ) : (
                <SubtitlePlaceholder />
              )}
              {value ? (
                <Description>{t(`createSurveyModal.${value}.surveyDescription`)}</Description>
              ) : (
                <DescriptionPlaceholder />
              )}
            </SurveyDescriptionTextContainer>
          </SurveyDescriptionContainer>
        )}
        name="survey"
      />
    </FormWrapper>
  );
};

export default CreateNewSurveyForm;
